import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import theme from '../../styles/theme';
import Button from './Button';
import VideoSetupGuide from './VideoSetupGuide';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const PopupContainer = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xxl};
  padding: ${theme.spacing.xxxl};
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.lg};
  width: 90%;
  max-width: 450px;
  position: relative;
  font-family: ${theme.typography.fontFamily};
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: none;
  border: none;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.xxl};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  line-height: 1;
  transition: color ${theme.transitions.normal};
  
  &:hover {
    color: ${theme.colors.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Title = styled.h2`
  margin: 0 0 ${theme.spacing.lg} 0;
  color: ${theme.colors.text};
  text-align: center;
  font-size: ${theme.typography.fontSizes.xxl};
  font-weight: ${theme.typography.fontWeights.bold};
  font-family: ${theme.typography.fontFamily};
`;

const Message = styled.p`
  margin: 0 0 ${theme.spacing.xl} 0;
  color: ${theme.colors.textSecondary};
  text-align: center;
  font-size: ${theme.typography.fontSizes.base};
  font-family: ${theme.typography.fontFamily};
  line-height: 1.5;
`;

const VideoName = styled.span`
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.fontWeights.medium};
  word-break: break-all;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.lg};
`;

const ProgressContainer = styled.div`
  margin: ${theme.spacing.lg} 0;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${theme.colors.background};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${theme.colors.primary};
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const ProgressText = styled.div`
  text-align: center;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.sm};
  margin-top: ${theme.spacing.sm};
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${theme.colors.background};
  border-top: 3px solid ${theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto ${theme.spacing.md};
`;

const SuccessMessage = styled.div`
  color: #50fa7b;
  font-size: ${theme.typography.fontSizes.base};
  text-align: center;
  padding: ${theme.spacing.md};
  background-color: rgba(80, 250, 123, 0.1);
  border-radius: ${theme.borderRadius.md};
  border: 1px solid #50fa7b;
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeights.medium};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.typography.fontSizes.sm};
  text-align: center;
  padding: ${theme.spacing.sm};
  background-color: rgba(255, 85, 85, 0.1);
  border-radius: ${theme.borderRadius.sm};
  border: 1px solid ${theme.colors.error};
  margin-top: ${theme.spacing.md};
`;

const ModeSelectorContainer = styled.div`
  margin: ${theme.spacing.lg} 0;
`;

const ModeLabel = styled.label`
  display: block;
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  margin-bottom: ${theme.spacing.sm};
  font-family: ${theme.typography.fontFamily};
`;

const ModeOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const ModeOptionContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const ModeOption = styled.label`
  display: flex;
  align-items: center;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.text};
  
  &:hover {
    border-color: ${theme.colors.primary};
    background-color: rgba(114, 210, 255, 0.1);
  }
  
  input[type="radio"] {
    margin-right: ${theme.spacing.sm};
    cursor: pointer;
  }
  
  input[type="radio"]:checked ~ ${ModeOptionContent} span {
    color: ${theme.colors.primary};
    font-weight: ${theme.typography.fontWeights.medium};
  }
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: auto;
`;

const TooltipIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${theme.colors.border};
  color: ${theme.colors.text};
  font-size: 12px;
  font-weight: ${theme.typography.fontWeights.bold};
  cursor: help;
  flex-shrink: 0;
  
  &:hover + div {
    opacity: 1;
    visibility: visible;
  }
`;

const TooltipContent = styled.div`
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background-color: ${theme.colors.background};
  color: ${theme.colors.text};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSizes.sm};
  line-height: 1.5;
  white-space: normal;
  width: 220px;
  border: 1px solid ${theme.colors.border};
  box-shadow: ${theme.shadows.lg};
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  z-index: 10;
  pointer-events: none;
`;

const InfoBox = styled.div`
  background-color: rgba(114, 210, 255, 0.08);
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.text};
  line-height: 1.6;
  font-family: ${theme.typography.fontFamily};
`;

const SetupGuideCard = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  width: 100%;
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  background-color: rgba(114, 210, 255, 0.06);
  border: 1px solid ${theme.colors.border};
  border-left: 3px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  text-align: left;
  font-family: ${theme.typography.fontFamily};

  &:hover {
    background-color: rgba(114, 210, 255, 0.12);
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.sm};
  }
`;

const SetupGuideThumbnail = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 42px;
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  border: 1px solid ${theme.colors.borderLight};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SetupGuideText = styled.div`
  flex: 1;
  min-width: 0;
`;

const SetupGuideTitle = styled.div`
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  line-height: 1.3;
`;

const SetupGuideSubtitle = styled.div`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.xs};
  margin-top: 2px;
`;

const SetupGuideChevron = styled.span`
  flex-shrink: 0;
  color: ${theme.colors.primary};
  font-size: ${theme.typography.fontSizes.xl};
  line-height: 1;
`;

const SendVideoPopup = ({ isOpen, onClose, onConfirm, videoName, isUploading, uploadProgress, uploadError, uploadSuccess }) => {
  const [selectedMode, setSelectedMode] = useState('singles');
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(selectedMode);
  };

  const handleClose = () => {
    if (!isUploading) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleClose}>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose} disabled={isUploading}>×</CloseButton>
        
        {showSetupGuide ? (
          <VideoSetupGuide onBack={() => setShowSetupGuide(false)} />
        ) : uploadSuccess ? (
          <SuccessMessage>
            ✓ Video uploaded successfully! It will be processed shortly.
          </SuccessMessage>
        ) : isUploading ? (
          <>
            <Spinner />
            <Message>
              Uploading <VideoName>{videoName || 'video'}</VideoName>...
            </Message>
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill progress={uploadProgress} />
              </ProgressBar>
              <ProgressText>{uploadProgress}% complete</ProgressText>
            </ProgressContainer>
          </>
        ) : (
          <>
            <Title>Send Video for Processing</Title>
            <Message>
              Are you sure you want to send{' '}
              <VideoName>{videoName || 'this video'}</VideoName>{' '}
              for processing?
            </Message>
            <InfoBox>
              This video will be sent to our processing queues. An email will be sent to you with a link to download the labels once it's done. Processing times are typically 2–4x realtime, but times vary based on how big your video is (we cap them at 2 hours) and how busy our queues are.
            </InfoBox>
            <SetupGuideCard onClick={() => setShowSetupGuide(true)}>
              <SetupGuideThumbnail>
                <img src="/images/camera_setup.svg" alt="" />
              </SetupGuideThumbnail>
              <SetupGuideText>
                <SetupGuideTitle>Camera Setup Guide</SetupGuideTitle>
                <SetupGuideSubtitle>View recommended camera positioning</SetupGuideSubtitle>
              </SetupGuideText>
              <SetupGuideChevron>›</SetupGuideChevron>
            </SetupGuideCard>
            <ModeSelectorContainer>
              <ModeLabel>Select Video Mode:</ModeLabel>
              <ModeOptions>
                <ModeOption>
                  <input
                    type="radio"
                    name="videoMode"
                    value="singles"
                    checked={selectedMode === 'singles'}
                    onChange={(e) => setSelectedMode(e.target.value)}
                  />
                  <ModeOptionContent>
                    <span>Singles</span>
                  </ModeOptionContent>
                  <TooltipWrapper>
                    <TooltipIcon>?</TooltipIcon>
                    <TooltipContent>A typical singles match.</TooltipContent>
                  </TooltipWrapper>
                </ModeOption>
                <ModeOption>
                  <input
                    type="radio"
                    name="videoMode"
                    value="workload_drills"
                    checked={selectedMode === 'workload_drills'}
                    onChange={(e) => setSelectedMode(e.target.value)}
                  />
                  <ModeOptionContent>
                    <span>Workload Drills</span>
                  </ModeOptionContent>
                  <TooltipWrapper>
                    <TooltipIcon>?</TooltipIcon>
                    <TooltipContent>We will capture one player's movement. No strokes, no balls.</TooltipContent>
                  </TooltipWrapper>
                </ModeOption>
                <ModeOption>
                  <input
                    type="radio"
                    name="videoMode"
                    value="stroke_drills"
                    checked={selectedMode === 'stroke_drills'}
                    onChange={(e) => setSelectedMode(e.target.value)}
                  />
                  <ModeOptionContent>
                    <span>Stroke Drills</span>
                  </ModeOptionContent>
                  <TooltipWrapper>
                    <TooltipIcon>?</TooltipIcon>
                    <TooltipContent>A player performing drills on one side of the court (closest to the camera). We will capture player movement, strokes, and bounces.</TooltipContent>
                  </TooltipWrapper>
                </ModeOption>
              </ModeOptions>
            </ModeSelectorContainer>
            {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
            <ButtonContainer>
              <Button
                type="button"
                onClick={handleClose}
                variant="ghost"
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                style={{ flex: 1 }}
              >
                Confirm
              </Button>
            </ButtonContainer>
          </>
        )}
        
        {uploadSuccess && (
          <ButtonContainer>
            <Button
              type="button"
              onClick={handleClose}
              style={{ flex: 1 }}
            >
              Close
            </Button>
          </ButtonContainer>
        )}
      </PopupContainer>
    </Overlay>
  );
};

export default SendVideoPopup;
